import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl, openAuthedFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MagnifyingGlass, FileArrowDown, Eye } from "@phosphor-icons/react";

export default function OrdersPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [q, setQ] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (isAdmin && repFilter !== "all") params.representante_id = repFilter;
    if (start) params.start = start;
    if (end) params.end = end;
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  }, [isAdmin, repFilter, start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "representante"))).catch(() => {});
  }, [isAdmin]);

  const filtered = orders.filter((o) =>
    !q ||
    o.numero?.toLowerCase().includes(q.toLowerCase()) ||
    o.cliente?.razao_social?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Pedidos</h1>
          <p className="text-muted-foreground mt-1">Histórico e emissão de pedidos.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" data-testid="export-orders-btn"
              onClick={() => openAuthedFile(`/orders-export`, "pedidos-dualt.xlsx")} className="gap-2">
              <FileArrowDown size={18} weight="bold" /> Exportar Excel
            </Button>
          )}
          <Button onClick={() => navigate("/pedidos/novo")} data-testid="new-order-btn"
            className="bg-accent hover:bg-accent/90 text-white gap-2">
            <Plus size={18} weight="bold" /> Novo Pedido
          </Button>
        </div>
      </div>

      <Card className="p-4 shadow-none border-border">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nº ou cliente..." className="pl-10" />
          </div>
          {isAdmin && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-rep"><SelectValue placeholder="Representante" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos representantes</SelectItem>
                {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-[150px]" data-testid="filter-start" />
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-[150px]" data-testid="filter-end" />
        </div>
      </Card>

      <Card className="shadow-none border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-zinc-50">
                {["Número", "Data", "Cliente", "Representante", "Prazo", "Total", "acoes"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 ${i >= 4 ? "text-right" : "text-left"}`}>{h === "acoes" ? "" : h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nenhum pedido encontrado.</td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/pedidos/${o.id}`)} data-testid={`order-row-${o.numero}`}>
                  <td className="px-4 py-3 font-mono font-medium text-zinc-900">{o.numero}</td>
                  <td className="px-4 py-3 text-zinc-600">{o.created_at?.slice(0, 10).split("-").reverse().join("/")}</td>
                  <td className="px-4 py-3 text-zinc-800 max-w-[220px] truncate">{o.cliente?.razao_social}</td>
                  <td className="px-4 py-3 text-zinc-600">{o.representante_nome}</td>
                  <td className="px-4 py-3 text-right">
                    <Badge variant="outline" className={o.prazo_valido ? "border-accent text-accent" : "border-destructive text-destructive"}>
                      {o.prazo || "—"} {o.prazo_valido ? "✓" : "!"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-zinc-900">{brl(o.total)}</td>
                  <td className="px-4 py-3 text-right"><Eye size={18} className="text-zinc-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
